import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { ADD_BOOKMARK, REMOVE_BOOKMARK } from "redux/bookmarks";
import { BOOKMARKABLE_TYPES, documentDetailPath } from "utils/chatDocumentTypes";
import isSafeUrl from "utils/isSafeUrl";
import sanitizeHtml from "utils/sanitizeHtml";
import trackEvent from "utils/trackEvent";
import SavePlus from "images/save_plus.png";
import Saved from "images/saved.png";

// Renders the structured documents[] returned alongside `answer` as a set
// of actionable cards - NOT more chat text - each clickable through to the
// document's own page, with a quick per-document bookmark toggle. The
// `answer` markdown already contains the same inline [text](url)
// citations; this is the richer, interactive view of the same documents.
export const CitationList = ({ documents = [], bookmarkedCodes, addBookmark, removeBookmark }) => {
  if (!documents.length) return null;

  return (
    <div className="citation-list">
      <div className="citation-list-heading">Related documents</div>
      <ul className="citation-cards">
        {documents.map(doc => {
          const detailPath = documentDetailPath(doc);
          const isBookmarkable = BOOKMARKABLE_TYPES.has(doc.type);
          const isBookmarked = bookmarkedCodes.includes(doc.code);

          const titleContent = (
            <>
              <span className="citation-title-text">{doc.title}</span>
              <span className="citation-open-arrow" aria-hidden="true">
                &rarr;
              </span>
            </>
          );

          return (
            <li key={doc.code} className="citation-card">
              {detailPath ? (
                <Link
                  to={detailPath}
                  className="citation-title"
                  onClick={() =>
                    trackEvent("CHAT_OPEN_DOCUMENT", { code: doc.code, type: doc.type })
                  }>
                  {titleContent}
                </Link>
              ) : isSafeUrl(doc.url) ? (
                <a
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="citation-title"
                  onClick={() =>
                    trackEvent("CHAT_OPEN_DOCUMENT", { code: doc.code, type: doc.type })
                  }>
                  {titleContent}
                </a>
              ) : (
                <span className="citation-title">{titleContent}</span>
              )}

              <div className="citation-meta">
                {doc.type && <span className="citation-type">{doc.type}</span>}
                {doc.customer && (
                  <span className="citation-customer">{doc.customer}</span>
                )}
              </div>

              {doc.summary_html ? (
                <div
                  className="citation-summary"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(doc.summary_html) }}
                />
              ) : (
                doc.summary && <div className="citation-summary">{doc.summary}</div>
              )}

              {isBookmarkable && (
                <button
                  type="button"
                  className={`citation-bookmark-btn ${
                    isBookmarked ? "is-bookmarked" : ""
                  }`}
                  title={isBookmarked ? "Remove bookmark" : "Bookmark this document"}
                  onClick={() => {
                    trackEvent("CHAT_TOGGLE_BOOKMARK", {
                      code: doc.code,
                      type: doc.type,
                      bookmarked: !isBookmarked,
                    });
                    if (isBookmarked) {
                      removeBookmark({ documentType: doc.type, documentId: doc.code });
                    } else {
                      addBookmark({ documentType: doc.type, documentId: doc.code });
                    }
                  }}>
                  <img
                    src={isBookmarked ? Saved : SavePlus}
                    alt={isBookmarked ? "Bookmarked" : "Bookmark"}
                  />
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default connect(
  ({ bookmarks }) => ({
    bookmarkedCodes: [
      ...(bookmarks?.offeringTopicsCodes || []),
      ...(bookmarks?.qualificationsCodes || []),
    ],
  }),
  dispatch => ({
    addBookmark: params => dispatch(ADD_BOOKMARK(params)),
    removeBookmark: params => dispatch(REMOVE_BOOKMARK(params)),
  })
)(CitationList);
